import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, X, Sparkles, Box } from 'lucide-react';
import { getFurnitureRoomTemplates } from '@/lib/api';
import { ItemPickerDialog } from '@/components/furniture/ItemPickerDialog';

// Canonical room types we always want to show first (in this order), even
// when no template has been saved yet for them. Any additional templates the
// user creates on the Room Defaults page get appended after these. 'Custom'
// is always the last tile so it stays out of the way of real room types.
const BUILTIN_ROOM_TYPES = [
  'Bedroom', 'Living Room', 'Dining Room', 'Living/Dining Room',
  'Kitchen', 'Patio', 'Bathroom',
];

export const AddRoomDialog = ({ open, onOpenChange, onAdd }) => {
  const [step, setStep] = useState('pick'); // 'pick' | 'customize'
  const [roomType, setRoomType] = useState('');
  const [customName, setCustomName] = useState('');
  const [templates, setTemplates] = useState({});
  const [items, setItems] = useState([]);
  const [essentials, setEssentials] = useState([]);
  const [newItemName, setNewItemName] = useState('');
  const [newItemEssential, setNewItemEssential] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep('pick'); setRoomType(''); setCustomName(''); setItems([]); setEssentials([]);
    getFurnitureRoomTemplates().then(setTemplates).catch(() => setTemplates({}));
  }, [open]);

  const tpl = templates[roomType] || { items: [], essentials: [], suggestions: { items: [], essentials: [] } };

  // Build the visible list of room types as: built-ins first (in canonical
  // order), then any user-created templates from Room Defaults, then a
  // trailing "Custom" tile for one-off rooms. This is what was missing —
  // previously the picker only ever showed the hardcoded built-ins.
  const roomTypes = useMemo(() => {
    const builtinSet = new Set(BUILTIN_ROOM_TYPES);
    const userTypes = Object.keys(templates).filter(
      (t) => !builtinSet.has(t) && t !== 'Custom'
    );
    return [...BUILTIN_ROOM_TYPES, ...userTypes, 'Custom'];
  }, [templates]);

  const selectType = (type) => {
    setRoomType(type);
    setCustomName(type === 'Custom' || type === 'Bathroom' ? '' : type);
    const t = templates[type] || { items: [], essentials: [] };
    setItems([...t.items]);
    setEssentials([...t.essentials]);
    setStep('customize');
  };

  // Suggestions that aren't already in the customised lists.
  const remainingSuggestions = useMemo(() => {
    const inUse = new Set([...items, ...essentials]);
    return {
      items: (tpl.suggestions?.items || []).filter((s) => !inUse.has(s)),
      essentials: (tpl.suggestions?.essentials || []).filter((s) => !inUse.has(s)),
    };
  }, [tpl, items, essentials]);

  const removeItem = (name) => setItems((xs) => xs.filter((x) => x !== name));
  const removeEssential = (name) => setEssentials((xs) => xs.filter((x) => x !== name));
  const addAsItem = (name) => {
    setItems((xs) => (xs.includes(name) ? xs : [...xs, name]));
    setEssentials((xs) => xs.filter((x) => x !== name));
  };
  const addAsEssential = (name) => {
    setEssentials((xs) => (xs.includes(name) ? xs : [...xs, name]));
    setItems((xs) => xs.filter((x) => x !== name));
  };

  const addCustom = () => {
    const n = newItemName.trim();
    if (!n) return;
    if (newItemEssential) addAsEssential(n);
    else addAsItem(n);
    setNewItemName('');
  };

  const submit = () => {
    const finalName = customName.trim() || roomType || 'New Room';
    // A row in the dialog is "Custom" only when (a) the user explicitly
    // picked the Custom tile, or (b) the type has no saved template (so
    // there is nothing to anchor it to). Any user-created Room Defaults
    // template — including Bathroom — should retain its own type.
    const templateExists = !!templates[roomType];
    const finalType = roomType === 'Custom' || !templateExists ? 'Custom' : roomType;
    // If the user landed on the customize step before the templates finished
    // loading (race), `items`/`essentials` will both be empty arrays even
    // though the room type DOES have defaults. In that case, omit them so
    // the backend re-hydrates from its own template and default-link
    // mappings still kick in.
    const userTouched = items.length > 0 || essentials.length > 0;
    if (userTouched || !templateExists) {
      onAdd(finalName, finalType, items, essentials);
    } else {
      onAdd(finalName, finalType, undefined, undefined);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="add-room-dialog">
        <DialogHeader>
          <DialogTitle>{step === 'pick' ? 'Add Room' : `Customize: ${roomType}`}</DialogTitle>
        </DialogHeader>

        {step === 'pick' ? (
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#5b6773]">Pick a room type. We'll pre-fill the standard items and let you tweak before adding.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {roomTypes.map((type) => (
                <Button
                  key={type}
                  variant="outline"
                  size="sm"
                  className="justify-start text-xs"
                  onClick={() => selectType(type)}
                  data-testid={`room-type-${type.toLowerCase().replace(/[\s/]/g, '-')}`}
                >
                  {type}
                </Button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4 overflow-y-auto pr-1 -mr-1 flex-1 pb-2">
            {/* Room name */}
            <div>
              <label className="text-xs font-medium text-[#5b6773] uppercase tracking-wider">Room Name</label>
              <Input
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder={roomType ? `e.g. ${roomType} 1` : 'Room name'}
                data-testid="room-name-input"
                className="mt-1.5"
              />
            </div>

            {/* Standard items */}
            <ItemList
              title="Standard Items"
              subtitle="Click × to remove any you don't want."
              items={items}
              onRemove={removeItem}
              chipClass="bg-emerald-50 text-emerald-800 border-emerald-200"
              testIdPrefix="standard"
              empty="No standard items — add some from suggestions or below."
            />

            {/* Essentials */}
            <ItemList
              title="Essentials"
              subtitle="Things every guest expects (linens, etc.)"
              items={essentials}
              onRemove={removeEssential}
              chipClass="bg-amber-50 text-amber-800 border-amber-200"
              testIdPrefix="essential"
              empty="No essentials yet — add some from suggestions or below."
            />

            {/* Suggestion chips */}
            {(remainingSuggestions.items.length > 0 || remainingSuggestions.essentials.length > 0) && (
              <div className="rounded-lg border border-dashed border-violet-200 bg-violet-50/40 p-3 space-y-3">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-violet-700 uppercase tracking-wider">
                  <Sparkles className="h-3 w-3" /> Quick add
                </div>
                {remainingSuggestions.items.length > 0 && (
                  <SuggestRow
                    label="Add to Standard:" chips={remainingSuggestions.items}
                    onClick={addAsItem} chipClass="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    testIdPrefix="suggest-item"
                  />
                )}
                {remainingSuggestions.essentials.length > 0 && (
                  <SuggestRow
                    label="Add as Essential:" chips={remainingSuggestions.essentials}
                    onClick={addAsEssential} chipClass="border-amber-300 text-amber-700 hover:bg-amber-50"
                    testIdPrefix="suggest-essential"
                  />
                )}
              </div>
            )}

            {/* Custom item input */}
            <div className="rounded-lg border bg-gray-50 p-3 space-y-2">
              <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Add a custom item</label>
              <div className="flex gap-2">
                <Input
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
                  placeholder="e.g. Foosball Table"
                  className="h-8 text-sm bg-white"
                  data-testid="add-room-custom-name"
                />
                <Button
                  type="button" size="sm" variant="outline" className="h-8"
                  onClick={() => setNewItemEssential((v) => !v)}
                  data-testid="add-room-custom-toggle"
                >
                  {newItemEssential ? 'Essential' : 'Standard'}
                </Button>
                <Button type="button" size="sm" className="h-8" onClick={addCustom} disabled={!newItemName.trim()} data-testid="add-room-custom-add">
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
              <Button
                type="button" size="sm" variant="outline" className="h-7 text-xs gap-1"
                onClick={() => setPickerOpen(true)}
                data-testid="add-room-pick-from-catalog"
              >
                <Box className="h-3 w-3" /> Add more items from catalog
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'customize' ? (
            <>
              <Button variant="outline" onClick={() => setStep('pick')}>← Back</Button>
              <Button
                onClick={submit}
                disabled={!customName.trim()}
                data-testid="add-room-submit"
              >
                Add Room ({items.length + essentials.length} items)
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          )}
        </DialogFooter>
      </DialogContent>
      <ItemPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title="Add items from catalog"
        existingNames={[...items, ...essentials]}
        onPick={async (name) => addAsItem(name)}
      />
    </Dialog>
  );
};

function ItemList({ title, subtitle, items, onRemove, chipClass, testIdPrefix, empty }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{title}</label>
        <span className="text-[10px] text-gray-400">{items.length}</span>
      </div>
      <p className="text-[11px] text-gray-500 mb-2">{subtitle}</p>
      {items.length === 0 ? (
        <p className="text-xs text-gray-400 italic px-1">{empty}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {items.map((name) => (
            <span
              key={name}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${chipClass}`}
              data-testid={`${testIdPrefix}-chip-${name.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {name}
              <button
                type="button"
                onClick={() => onRemove(name)}
                className="hover:opacity-60 transition-opacity"
                aria-label={`Remove ${name}`}
                data-testid={`${testIdPrefix}-remove-${name.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SuggestRow({ label, chips, onClick, chipClass, testIdPrefix }) {
  return (
    <div>
      <p className="text-[11px] text-gray-600 mb-1.5">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {chips.map((c) => (
          <button
            key={c} type="button"
            onClick={() => onClick(c)}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border bg-white transition-colors ${chipClass}`}
            data-testid={`${testIdPrefix}-${c.replace(/\s+/g, '-').toLowerCase()}`}
          >
            <Plus className="h-3 w-3" /> {c}
          </button>
        ))}
      </div>
    </div>
  );
}
